/**
 * Per-device portal-entry wizard for online-reporting purveyors.
 *
 * OWNERSHIP (#796, Corbin 2026-08-16): filing with the jurisdiction's portal
 * is the **OFFICE's** job, not the tech's. This panel still renders on the
 * tech's job screen because it holds the per-device cheat-sheet numbers and
 * the device-stamped photos, and a tech who happens to be in the portal can
 * use it — but it must never read as the tech's outstanding work. The audit
 * caught a fully passed, closed-out job showing a bare "0 of 1 done", which
 * looked exactly like an unfinished tech task. The count is now owner-labelled
 * ("Office: 0 of 1 filed") and the panel says plainly that nothing here is
 * waiting on the tech.
 *
 * The work it sequences, one device at a time, so nothing relies on memory:
 *
 *   "Device 2 of 3 — A827180" → its cheat-sheet numbers → [Send N photos
 *   to Photos] (Web Share with the device-STAMPED variants — identity is
 *   burned into the pixels so it survives iOS Photo Library) → whoever is
 *   filing switches to the portal tab, uploads from Recents → back →
 *   [Done, next device].
 *
 * #1665 — devices released without a test ("Couldn't test" / "Not located")
 * follow the tested ones as "Not tested i of n" steps: reason, identity,
 * photos, no readings. They count toward the office's filed total and file
 * like any device (Corbin, 2026-09-30).
 *
 * Per-device checkmarks persist server-side
 * (submission_records.portal_entry_done_at) so app-switching or a dead
 * battery never loses the place. Fallbacks for no-Photos-permission:
 * per-photo / per-device saves with canonical filenames and a save-all
 * zip. Installed-PWA quirk: when file sharing isn't available, an "Open
 * in Safari" action mints a short-lived link to reopen this job here.
 */
import { Fragment, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PortalPhotoHandoff } from "@/components/portal-photo-handoff";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Check, CheckCircle2, ChevronLeft, ChevronRight, Copy, Globe, Loader2 } from "lucide-react";

type PortalEntryPhoto = { id: string; fileName: string };

type PortalEntryDevice = {
    assetId: string;
    index: number;
    serial: string | null;
    sizeLabel: string | null;
    assemblyType: string | null;
    location: string | null;
    result: "pass" | "fail";
    readings: Array<{ label: string; value: string }>;
    retestReadings: Array<{ label: string; value: string }> | null;
    repairs: string | null;
    remarks: string | null;
    doneAt: string | null;
    photos: PortalEntryPhoto[];
};

/** #1665 — a device released without a test ("Couldn't test" / "Not
 *  located"). It still has to be entered, as not tested, so it gets a step
 *  after the tested devices, like the cheat sheet's "Not tested" section. */
type PortalEntryNotTested = {
    assetId: string;
    index: number;
    disposition: "could_not_test" | "not_located";
    serial: string | null;
    sizeLabel: string | null;
    assemblyType: string | null;
    location: string | null;
    manufacturer: string | null;
    model: string | null;
    reason: string;
    markedBy: string | null;
    doneAt: string | null;
    photos: PortalEntryPhoto[];
};

type PortalEntryResponse = {
    isPortal: boolean;
    providerName: string | null;
    platformName: string | null;
    portalUrl: string | null;
    accountNumber: { number: string; source: "site" | "directory" } | null;
    devices: PortalEntryDevice[];
    /** Absent from servers older than #1665. */
    notTested?: PortalEntryNotTested[];
};

type Step = ({ kind: "tested" } & PortalEntryDevice) | ({ kind: "not_tested" } & PortalEntryNotTested);

export function PortalEntryWizard({ jobId }: { jobId: string }) {
    const queryClient = useQueryClient();
    const [stepOverride, setStepOverride] = useState<number | null>(null);
    const [safariLink, setSafariLink] = useState<string | null>(null);

    const entry = useQuery({
        queryKey: ["backflow", "portal-entry", jobId],
        queryFn: () => api.get<PortalEntryResponse>(`/api/backflow/job/${jobId}/portal-entry`),
        enabled: !!jobId,
    });

    const markDone = useMutation({
        mutationFn: (args: { assetId: string; done: boolean }) =>
            api.patch(`/api/backflow/job/${jobId}/portal-entry/${args.assetId}`, { done: args.done }),
        onSuccess: (_res, args) => {
            queryClient.invalidateQueries({ queryKey: ["backflow", "portal-entry", jobId] });
            if (args.done) setStepOverride(null); // advance to the next not-done device
        },
        onError: () => toast.error("Could not save portal-entry progress"),
    });

    const devices = entry.data?.devices ?? [];
    const notTested = entry.data?.notTested ?? [];
    const steps: Step[] = useMemo(
        () => [
            ...devices.map((d) => ({ kind: "tested" as const, ...d })),
            ...notTested.map((d) => ({ kind: "not_tested" as const, ...d })),
        ],
        [devices, notTested],
    );
    const doneCount = steps.filter((d) => d.doneAt).length;
    const allDone = steps.length > 0 && doneCount === steps.length;

    // Current step: explicit navigation wins; otherwise the first not-done
    // device (server-persisted, so an interruption resumes correctly).
    const currentIndex = useMemo(() => {
        if (stepOverride !== null) return Math.min(stepOverride, steps.length - 1);
        const firstOpen = steps.findIndex((d) => !d.doneAt);
        return firstOpen >= 0 ? firstOpen : steps.length - 1;
    }, [stepOverride, steps]);

    if (!entry.data || !entry.data.isPortal || steps.length === 0) return null;
    const data = entry.data;
    const device = steps[currentIndex]!;
    const notLocated = device.kind === "not_tested" && device.disposition === "not_located";

    const openInSafari = async () => {
        try {
            const res = await api.post<{ url: string }>(`/api/backflow/job/${jobId}/portal-entry-link`);
            setSafariLink(res.url);
            // Same-origin links stay inside the installed PWA on iOS, so we
            // can't force a real Safari window programmatically — surface the
            // link with copy so the tech can paste it into Safari.
            window.open(res.url, "_blank", "noopener");
        } catch {
            toast.error("Could not create the Safari link");
        }
    };

    return (
        <Card>
            <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-primary shrink-0" />
                    <div className="text-xs uppercase tracking-wider text-foreground/80 font-semibold flex-1">
                        Portal entry — the office files this
                    </div>
                    {/* #796 (audit scr 21) — this badge used to read "0 of 1
                        done" with no owner on it, so on a fully passed,
                        closed-out job it looked like the TECH's unfinished
                        task. Corbin, 2026-08-16: portal filing is the OFFICE's
                        job, and it must not hang the tech up. Naming the owner
                        is what stops a count from reading as a to-do. */}
                    <Badge variant={allDone ? "default" : "secondary"} className="text-[10px]">
                        Office: {doneCount} of {steps.length} filed
                    </Badge>
                </div>

                <p className="text-xs text-foreground/80" data-testid="portal-entry-owner-note">
                    {allDone
                        ? "All filed with the city. Nothing outstanding on this job."
                        : "The office files these with the city — your part of this job is finished and nothing here is waiting on you. If you're already in the portal, the numbers and photos below are here to help."}
                </p>

                <div className="text-xs text-foreground/80 leading-snug">
                    {[data.providerName, data.platformName ? `via ${data.platformName}` : null]
                        .filter(Boolean)
                        .join(" · ") || "Online reporting"}
                    {data.accountNumber && (
                        <>
                            {" · "}
                            <span className="font-medium text-foreground">
                                Acct #{data.accountNumber.number}
                                {data.accountNumber.source === "directory" ? " (from directory)" : ""}
                            </span>
                        </>
                    )}
                </div>

                {/* Per-device step dots */}
                <div className="flex flex-wrap items-center gap-1.5">
                    {steps.map((d, i) => (
                        <Fragment key={d.assetId}>
                            {/* Divider before the first not-tested step. */}
                            {d.kind === "not_tested" && i === devices.length && devices.length > 0 && (
                                <span className="h-5 w-px bg-border mx-0.5" aria-hidden />
                            )}
                            <button
                                type="button"
                                onClick={() => setStepOverride(i)}
                                className={cn(
                                    "h-7 min-w-7 px-1 rounded-full border text-xs font-semibold flex items-center justify-center",
                                    d.kind === "not_tested" && "border-dashed",
                                    d.doneAt
                                        ? "bg-emerald-700/90 border-emerald-600 text-white"
                                        : i === currentIndex
                                          ? "border-primary text-primary"
                                          : "border-border text-foreground/70",
                                )}
                                aria-label={d.kind === "tested" ? `Device ${d.index}` : `Not tested ${d.index}`}
                            >
                                {d.doneAt ? <Check className="h-3.5 w-3.5" /> : d.kind === "tested" ? d.index : "—"}
                            </button>
                        </Fragment>
                    ))}
                </div>

                {allDone ? (
                    // Recap: everything saved, with the cleanup reminder.
                    <div className="rounded-lg border border-emerald-600/40 bg-emerald-500/10 p-3 space-y-2">
                        <div className="flex items-center gap-2 text-sm font-medium">
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                            All {steps.length} device{steps.length === 1 ? "" : "s"} entered
                            {notTested.length > 0 && (
                                <span className="font-normal text-foreground/80">
                                    ({devices.length} tested · {notTested.length} not tested)
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-foreground/80 space-y-1">
                            <div>Photos saved during this entry:</div>
                            <ul className="list-disc pl-4">
                                {steps
                                    .flatMap((d) => d.photos)
                                    .map((p) => (
                                        <li key={p.id} className="font-mono text-[11px]">
                                            {p.fileName}
                                        </li>
                                    ))}
                            </ul>
                        </div>
                    </div>
                ) : (
                    <div
                        className={cn("rounded-lg border p-3 space-y-3", device.kind === "not_tested" && "bg-muted/60")}
                    >
                        <div className="flex items-start justify-between gap-2">
                            <div>
                                <div className="font-semibold text-sm">
                                    {device.kind === "tested"
                                        ? `Device ${device.index} of ${devices.length}`
                                        : `Not tested ${device.index} of ${notTested.length}`}
                                    {device.serial ? ` — ${device.serial}` : ""}
                                </div>
                                <div className="text-xs text-foreground/80">
                                    {[device.sizeLabel, device.assemblyType, device.location]
                                        .filter(Boolean)
                                        .join(" · ") || "—"}
                                </div>
                            </div>
                            {device.kind === "tested" ? (
                                <Badge
                                    className={cn(
                                        "text-[10px] shrink-0",
                                        device.result === "fail"
                                            ? "bg-red-600 text-white hover:bg-red-700"
                                            : "bg-emerald-700 text-white hover:bg-emerald-800",
                                    )}
                                >
                                    {device.result === "fail" ? "FAIL" : "PASS"}
                                </Badge>
                            ) : (
                                <Badge className="text-[10px] shrink-0 bg-stone-600 text-white hover:bg-stone-700">
                                    {notLocated ? "NOT LOCATED" : "NOT TESTED"}
                                </Badge>
                            )}
                        </div>

                        {device.kind === "not_tested" ? (
                            <div className="space-y-2" data-testid="portal-entry-not-tested">
                                <div className="border-l-[3px] border-stone-500 pl-2.5 text-sm">
                                    <div className="font-semibold">{device.reason}</div>
                                    {device.markedBy && (
                                        <div className="text-xs text-foreground/80">{device.markedBy}</div>
                                    )}
                                </div>
                                <div className="text-sm space-y-0.5">
                                    {[
                                        { label: "Make", value: device.manufacturer },
                                        { label: "Model", value: device.model },
                                        { label: "Serial #", value: device.serial },
                                    ].map((r) => (
                                        <div key={r.label} className="flex justify-between gap-3">
                                            <span className="text-foreground/80">{r.label}</span>
                                            <span className="font-medium text-right">{r.value ?? "—"}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="rounded-md border bg-background p-2 text-xs text-foreground/80">
                                    No readings. Enter this device in the portal as{" "}
                                    {notLocated ? "not located" : "not tested"}, with the reason above.
                                </div>
                            </div>
                        ) : (
                            /* Cheat-sheet numbers, labeled like the portal fields */
                            <div className="text-sm space-y-0.5">
                                {device.readings.map((r) => (
                                    <div key={r.label} className="flex justify-between gap-3">
                                        <span className="text-foreground/80">{r.label}</span>
                                        <span className="font-medium text-right">{r.value}</span>
                                    </div>
                                ))}
                                {device.repairs && (
                                    <div className="pt-1 text-xs text-foreground/80">Repairs: {device.repairs}</div>
                                )}
                                {device.retestReadings?.map((r) => (
                                    <div key={`retest-${r.label}`} className="flex justify-between gap-3">
                                        <span className="text-foreground/80">Retest — {r.label}</span>
                                        <span className="font-medium text-right">{r.value}</span>
                                    </div>
                                ))}
                                {device.remarks && (
                                    <div className="pt-1 text-xs text-foreground/80">Remarks: {device.remarks}</div>
                                )}
                            </div>
                        )}

                        {/* Photos: the shared hand-off (#1100). The office
                            keeps the zip — a desktop can actually unpack one —
                            while the tester's filing card saves separate files
                            a portal's picker can read. */}
                        <PortalPhotoHandoff
                            photos={device.photos}
                            stampLabel={[device.serial, device.sizeLabel, device.assemblyType]
                                .filter(Boolean)
                                .join(" · ")}
                            bulkSave="zip"
                            onOpenInSafari={openInSafari}
                            empty="No photos captured for this device."
                        />
                        {safariLink && (
                            <button
                                type="button"
                                className="w-full text-left text-xs text-primary underline break-all"
                                onClick={() => {
                                    navigator.clipboard
                                        ?.writeText(safariLink)
                                        .then(() => toast.success("Link copied — paste it in Safari"))
                                        .catch(() => toast.error("Could not copy the link"));
                                }}
                            >
                                <Copy className="h-3 w-3 inline mr-1" />
                                Copy Safari link (valid 30 min)
                            </button>
                        )}

                        {/* Navigation + done */}
                        <div className="flex items-center gap-2 pt-1">
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-11 w-11 shrink-0"
                                disabled={currentIndex === 0}
                                onClick={() => setStepOverride(currentIndex - 1)}
                                aria-label="Previous device"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            {device.doneAt ? (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="lg"
                                    className="flex-1 h-11"
                                    disabled={markDone.isPending}
                                    onClick={() => markDone.mutate({ assetId: device.assetId, done: false })}
                                >
                                    <Check className="h-4 w-4 mr-2" />
                                    Done — tap to reopen
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    size="lg"
                                    className="flex-1 h-11"
                                    disabled={markDone.isPending}
                                    onClick={() => markDone.mutate({ assetId: device.assetId, done: true })}
                                >
                                    {markDone.isPending ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Check className="h-4 w-4 mr-2" />
                                    )}
                                    Done{currentIndex < steps.length - 1 ? ", next device" : ""}
                                </Button>
                            )}
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-11 w-11 shrink-0"
                                disabled={currentIndex >= steps.length - 1}
                                onClick={() => setStepOverride(currentIndex + 1)}
                                aria-label="Next device"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
